import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useI18n } from "@/i18n";
import * as store from "@/lib/decisionStore";
import { getFlags } from "@/lib/flags";
import { getConsent } from "@/lib/mockAuth";

export function useWorkspace() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState("loading");
  const [decision, setDecision] = useState(null);
  const [busy, setBusy] = useState("");
  const [errors, setErrors] = useState({});
  const [relatedChapters, setRelatedChapters] = useState([]);
  const [relatedLocked, setRelatedLocked] = useState(false);
  const autoCompose = useRef(!!location.state?.compose);
  const flags = getFlags();
  const aiOn = !!getConsent()?.ai;

  const loadRelated = useCallback(async (decId, tags) => {
    if (!tags || !Array.isArray(tags) || tags.length === 0) {
      setRelatedChapters([]);
      setRelatedLocked(false);
      return;
    }
    const rel = await store.getRelatedChapters(decId);
    if (rel && rel.chapters) {
      setRelatedChapters(rel.chapters);
      setRelatedLocked(!!rel.locked);
    } else {
      setRelatedChapters([]);
      setRelatedLocked(false);
    }
  }, []);

  const load = useCallback(async () => {
    setState("loading");
    const res = await store.getDecision(id);
    if (res.error) return setState(res.error === "missing" ? "missing" : "error");
    setDecision(res.decision);
    setState("ready");
    loadRelated(id, res.decision.tags);
  }, [id, loadRelated]);

  useEffect(() => {
    load();
  }, [load]);

  const run = useCallback(
    async (key, errKey, fn) => {
      setBusy(key);
      setErrors({});
      const res = await fn();
      setBusy("");
      if (res.error) {
        setErrors({ [key]: t(res.error === "maxpin" ? "err.pinMax" : errKey) });
        return null;
      }
      if (res.decision) {
        setDecision(res.decision);
        loadRelated(id, res.decision.tags);
      }
      return res;
    },
    [t, id, loadRelated],
  );

  const left = decision ? store.callsLeft(decision) : 4;
  const committed = decision?.status === "committed" || decision?.status === "deferred";
  const blocker = flags.locked ? "locked" : flags.aiPaused ? "kill" : !aiOn ? "noConsent" : left <= 0 ? "quotaOut" : null;
  const canAi = !blocker;

  const compose = useCallback(
    () => run("compose", "err.compose", () => store.composeBrief(id, lang)),
    [run, id, lang],
  );

  useEffect(() => {
    if (state === "ready" && autoCompose.current && canAi) {
      autoCompose.current = false;
      navigate(location.pathname, { replace: true, state: null });
      compose();
    }
  }, [state, canAi, compose, navigate, location.pathname]);

  return {
    id, state, decision, busy, errors, left, committed, blocker, canAi, aiOn, load, compose,
    relatedChapters, relatedLocked,
    challenge: (mode) => run("challenge", "err.challenge", () => store.runChallenge(id, mode, lang)),
    continuity: () => run("continuity", "err.continuity", () => store.checkContinuity(id, lang)),
    changeStatus: (itemId, ep) => run("item", "err.saveItem", () => store.setItemStatus(id, itemId, ep)),
    addItem: (text, epistemic, source = "user") =>
      run("item", "err.saveItem", () => store.addItem(id, { text, epistemic, source })),
    deleteOption: (oid) => run("option", "err.deleteOption", () => store.deleteOption(id, oid)),
    togglePin: () => run("pin", "err.pin", () => store.togglePin(id)),
    setChooseBy: (day) => run("chooseBy", "chooseBy.error", () => store.setChooseBy(id, day)),
    setTriggerChecked: (index, checked) => run("trigger", "trigger.error", () =>
      store.setBriefTriggerChecked(id, decision.brief.id, index, checked)),
  };
}
